/**
 * Authentication service with httpOnly cookie support
 *
 * Security: Tokens stored in httpOnly cookies (XSS-protected)
 * No localStorage token storage - cookies managed by browser
 */
import api from './api';
import { ShareQuota } from '../types/share.types';

export interface User {
  id: string;
  email: string;
  username: string;
  avatar?: string;
  role: 'user' | 'admin';
  invites_available: number;
  created_at: string;
  last_login?: string;
  is_blocked: boolean;
  share_quota?: ShareQuota;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
  is_new_user: boolean;
}

export interface AuthCheckResponse {
  authenticated: boolean;
  user_id: string | null;
}

class AuthService {
  /**
   * Step 1: Initiate Google OAuth login
   * Redirects to backend which redirects to Google
   */
  initiateGoogleLogin() {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8027';
    window.location.href = `${apiUrl}/api/auth/google/login`;
  }

  /**
   * Step 2: Complete login after Google OAuth
   * Called with code from Google callback
   * Backend sets httpOnly cookies automatically
   */
  async completeLogin(
    code: string,
    inviteCode: string,
    turnstileToken: string
  ): Promise<LoginResponse> {
    const body: any = { code };

    if (inviteCode) {
      body.invite_code = inviteCode;
    }

    if (turnstileToken) {
      body.turnstile_token = turnstileToken;
    }

    const response = await api.post<LoginResponse>('/auth/complete-login', body);
    // Tokens are set as httpOnly cookies by backend - no localStorage needed
    return response.data;
  }

  /**
   * Check authentication status via httpOnly cookie
   * Returns true if user is authenticated
   */
  async checkAuth(): Promise<AuthCheckResponse> {
    try {
      const response = await api.get<AuthCheckResponse>('/auth/check');
      return response.data;
    } catch {
      return { authenticated: false, user_id: null };
    }
  }

  /**
   * Get current user profile
   */
  async getMe(): Promise<User> {
    const response = await api.get<User>('/users/me');
    return response.data;
  }

  /**
   * Update current user profile
   */
  async updateProfile(data: { username?: string }): Promise<User> {
    const response = await api.patch<User>('/users/me', data);
    return response.data;
  }

  /**
   * Logout user
   * Backend clears httpOnly cookies
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors - cookies will be cleared by backend
    }
  }
}

export const authService = new AuthService();
export default authService;
